import { Injectable } from '@angular/core';
import { ImpersonationAuditLogEntity } from 'mj_generatedentities';
import { UserInfo } from '@memberjunction/core';
import { DataFetchService } from './data-fetch.service';
import { EntityNames } from '../utils/constants';
import { Utils } from '../utils/utils';

/**
 * Audit action types for impersonation logging
 */
export type AuditActionType = 
  | 'SessionStart'
  | 'SessionEnd' 
  | 'SessionExpire'
  | 'SessionExtend'
  | 'PageAccess'
  | 'ActionPerformed'
  | 'PermissionCheck'
  | 'ForceEnd';

/**
 * Audit log entry interface
 */
export interface ImpersonationAuditEntry {
  id: string;
  impersonationSessionId: string;
  originalUserId: string;
  originalUserName: string;
  impersonatedUserId: string;
  impersonatedUserName: string;
  auditAction: AuditActionType;
  actionDetails: any;
  timestamp: Date;
  clientIP: string | null;
  userAgent: string | null;
}

/**
 * Action details for different audit types
 */
export interface SessionStartDetails {
  reason?: string;
  sessionDuration?: number;
  permissions?: string[];
}

export interface SessionEndDetails {
  reason: 'manual' | 'expired' | 'forced';
  sessionDuration: number;
  actionsPerformed?: number;
}

export interface PageAccessDetails {
  route: string;
  component: string;
  parameters?: Record<string, any>;
}

export interface ActionPerformedDetails {
  action: string;
  entity?: string;
  recordId?: string;
  success: boolean;
  details?: any;
}

export interface PermissionCheckDetails {
  permission: string;
  granted: boolean;
  rule?: string;
}

/**
 * Service for comprehensive impersonation audit logging
 * Provides secure, compliant audit trail for all impersonation activities
 */
@Injectable({
  providedIn: 'root'
})
export class ImpersonationAuditService {

  constructor(private dataFetchService: DataFetchService, private utils: Utils) { }

  /**
   * Logs the start of an impersonation session
   * @param sessionId - Unique session identifier
   * @param originalUser - Staff user performing impersonation
   * @param impersonatedUser - User being impersonated
   * @param details - Additional session start details
   * @returns Promise resolving to audit log entry
   */
  public async logSessionStart(
    sessionId: string,
    originalUser: UserInfo,
    impersonatedUser: UserInfo,
    details: SessionStartDetails = {}
  ): Promise<ImpersonationAuditLogEntity | null> {
    return this.createAuditEntry(
      sessionId,
      originalUser,
      impersonatedUser,
      'SessionStart',
      details
    );
  }

  /**
   * Logs the end of an impersonation session
   * @param sessionId - Session identifier
   * @param originalUser - Staff user
   * @param impersonatedUser - Impersonated user
   * @param details - Session end details
   * @returns Promise resolving to audit log entry
   */
  public async logSessionEnd(
    sessionId: string,
    originalUser: UserInfo,
    impersonatedUser: UserInfo,
    details: SessionEndDetails
  ): Promise<ImpersonationAuditLogEntity | null> {
    return this.createAuditEntry(
      sessionId,
      originalUser,
      impersonatedUser,
      'SessionEnd',
      details
    );
  }

  /**
   * Logs session expiration
   * @param sessionId - Session identifier
   * @param originalUser - Staff user
   * @param impersonatedUser - Impersonated user
   * @param sessionDuration - How long session lasted
   * @returns Promise resolving to audit log entry
   */
  public async logSessionExpire(
    sessionId: string,
    originalUser: UserInfo,
    impersonatedUser: UserInfo,
    sessionDuration: number
  ): Promise<ImpersonationAuditLogEntity | null> {
    return this.createAuditEntry(
      sessionId,
      originalUser,
      impersonatedUser,
      'SessionExpire',
      { reason: 'expired', sessionDuration }
    );
  }

  /**
   * Logs session extension
   * @param sessionId - Session identifier
   * @param originalUser - Staff user
   * @param impersonatedUser - Impersonated user
   * @param newDuration - New session duration
   * @returns Promise resolving to audit log entry
   */
  public async logSessionExtend(
    sessionId: string,
    originalUser: UserInfo,
    impersonatedUser: UserInfo,
    newDuration: number
  ): Promise<ImpersonationAuditLogEntity | null> {
    return this.createAuditEntry(
      sessionId,
      originalUser,
      impersonatedUser,
      'SessionExtend',
      { newDuration }
    );
  }

  /**
   * Logs page access during impersonation
   * @param sessionId - Session identifier
   * @param originalUser - Staff user
   * @param impersonatedUser - Impersonated user
   * @param details - Page access details
   * @returns Promise resolving to audit log entry
   */
  public async logPageAccess(
    sessionId: string,
    originalUser: UserInfo,
    impersonatedUser: UserInfo,
    details: PageAccessDetails
  ): Promise<ImpersonationAuditLogEntity | null> {
    return this.createAuditEntry(
      sessionId,
      originalUser,
      impersonatedUser,
      'PageAccess',
      details
    );
  }

  /**
   * Logs actions performed during impersonation
   * @param sessionId - Session identifier
   * @param originalUser - Staff user
   * @param impersonatedUser - Impersonated user
   * @param details - Action details
   * @returns Promise resolving to audit log entry
   */
  public async logActionPerformed(
    sessionId: string,
    originalUser: UserInfo,
    impersonatedUser: UserInfo,
    details: ActionPerformedDetails
  ): Promise<ImpersonationAuditLogEntity | null> {
    return this.createAuditEntry(
      sessionId,
      originalUser,
      impersonatedUser,
      'ActionPerformed',
      details
    );
  }

  /**
   * Logs permission checks during impersonation
   * @param sessionId - Session identifier
   * @param originalUser - Staff user
   * @param impersonatedUser - Impersonated user
   * @param details - Permission check details
   * @returns Promise resolving to audit log entry
   */
  public async logPermissionCheck(
    sessionId: string,
    originalUser: UserInfo,
    impersonatedUser: UserInfo,
    details: PermissionCheckDetails
  ): Promise<ImpersonationAuditLogEntity | null> {
    return this.createAuditEntry(
      sessionId,
      originalUser,
      impersonatedUser,
      'PermissionCheck',
      details
    );
  }

  /**
   * Logs forced session termination by admin
   * @param sessionId - Session identifier
   * @param originalUser - Staff user
   * @param impersonatedUser - Impersonated user
   * @param adminUser - Admin who forced the termination
   * @returns Promise resolving to audit log entry
   */
  public async logForceEnd(
    sessionId: string,
    originalUser: UserInfo,
    impersonatedUser: UserInfo,
    adminUser: UserInfo
  ): Promise<ImpersonationAuditLogEntity | null> {
    return this.createAuditEntry(
      sessionId,
      originalUser,
      impersonatedUser,
      'ForceEnd',
      { forcedByUser: adminUser.Name, forcedByUserId: adminUser.ID }
    );
  }

  /**
   * Gets audit log entries for a specific session
   * @param sessionId - Session identifier
   * @returns Promise resolving to array of audit entries
   */
  public async getSessionAuditLog(sessionId: string): Promise<ImpersonationAuditEntry[]> {
    try {
      // Use parallel queries to get audit logs and users
      const [auditLogsResult, usersResult] = await this.dataFetchService.fetchMultipleEntities([
        {
          EntityName: EntityNames.ImpersonationAuditLogs,
          ExtraFilter: `ImpersonationSessionID = '${sessionId}'`,
          OrderBy: 'Timestamp ASC',
          ResultType: 'entity_object'
        },
        {
          EntityName: EntityNames.Users,
          ResultType: 'entity_object'
        }
      ]);

      if (!auditLogsResult.success || !usersResult.success) {
        console.error('Error fetching session audit log or users');
        return [];
      }

      const auditLogs = auditLogsResult.data as ImpersonationAuditLogEntity[];
      const users = usersResult.data as any[];
      
      // Create user lookup map for performance
      const userMap = new Map();
      users.forEach(user => userMap.set(user.ID, user.Name));

      return auditLogs.map(log => ({
        id: log.ID!,
        impersonationSessionId: log.ImpersonationSessionID,
        originalUserId: log.OriginalUser_ID,
        originalUserName: userMap.get(log.OriginalUser_ID) || 'Unknown User',
        impersonatedUserId: log.ImpersonatedUser_ID,
        impersonatedUserName: userMap.get(log.ImpersonatedUser_ID) || 'Unknown User',
        auditAction: log.AuditAction as AuditActionType,
        actionDetails: this.parseActionDetails(log.ActionDetails),
        timestamp: new Date(log.Timestamp),
        clientIP: log.ClientIP,
        userAgent: log.UserAgent
      }));
    } catch (error) {
      console.error('Error fetching session audit log:', error);
      return [];
    }
  }

  /**
   * Gets audit log entries for a specific user (either as impersonator or impersonated)
   * @param userId - User ID
   * @param dateRange - Optional date range filter
   * @returns Promise resolving to array of audit entries
   */
  public async getUserAuditLog(
    userId: string,
    dateRange?: { start: Date; end: Date }
  ): Promise<ImpersonationAuditEntry[]> {
    try {
      // Build filter for user involvement and optional date range
      let filter = `(OriginalUser_ID = '${userId}' OR ImpersonatedUser_ID = '${userId}')`;
      
      if (dateRange) {
        const startDate = dateRange.start.toISOString();
        const endDate = dateRange.end.toISOString();
        filter += ` AND Timestamp >= '${startDate}' AND Timestamp <= '${endDate}'`;
      }

      // Use parallel queries to get audit logs and users
      const [auditLogsResult, usersResult] = await this.dataFetchService.fetchMultipleEntities([
        {
          EntityName: EntityNames.ImpersonationAuditLogs,
          ExtraFilter: filter,
          OrderBy: 'Timestamp DESC',
          ResultType: 'entity_object'
        },
        {
          EntityName: EntityNames.Users,
          ResultType: 'entity_object'
        }
      ]);

      if (!auditLogsResult.success || !usersResult.success) {
        console.error('Error fetching user audit log or users');
        return [];
      }

      const auditLogs = auditLogsResult.data as ImpersonationAuditLogEntity[];
      const users = usersResult.data as any[];
      
      // Create user lookup map for performance
      const userMap = new Map();
      users.forEach(user => userMap.set(user.ID, user.Name));

      return auditLogs.map(log => ({
        id: log.ID!,
        impersonationSessionId: log.ImpersonationSessionID,
        originalUserId: log.OriginalUser_ID,
        originalUserName: userMap.get(log.OriginalUser_ID) || 'Unknown User',
        impersonatedUserId: log.ImpersonatedUser_ID,
        impersonatedUserName: userMap.get(log.ImpersonatedUser_ID) || 'Unknown User',
        auditAction: log.AuditAction as AuditActionType,
        actionDetails: this.parseActionDetails(log.ActionDetails),
        timestamp: new Date(log.Timestamp),
        clientIP: log.ClientIP,
        userAgent: log.UserAgent
      }));
    } catch (error) {
      console.error('Error fetching user audit log:', error);
      return [];
    }
  }

  /**
   * Exports audit logs for compliance reporting
   * @param filters - Optional filters for the export
   * @returns Promise resolving to CSV string
   */
  public async exportAuditLogs(filters?: {
    dateRange?: { start: Date; end: Date };
    userId?: string;
    sessionId?: string;
    actionType?: AuditActionType;
  }): Promise<string> {
    try {
      const auditEntries = await this.getFilteredAuditLogs(filters);
      
      // Convert to CSV format
      const headers = [
        'Timestamp',
        'Session ID',
        'Original User',
        'Impersonated User',
        'Action',
        'Details',
        'Client IP',
        'User Agent'
      ];

      const csvRows = [
        headers.join(','),
        ...auditEntries.map(entry => [
          entry.timestamp.toISOString(),
          entry.impersonationSessionId,
          `"${entry.originalUserName}"`,
          `"${entry.impersonatedUserName}"`,
          entry.auditAction,
          `"${JSON.stringify(entry.actionDetails)}"`,
          entry.clientIP || '',
          `"${entry.userAgent || ''}"`
        ].join(','))
      ];

      return csvRows.join('\n');
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      throw error;
    }
  }

  /**
   * Creates a new audit log entry
   * @param sessionId - Session identifier
   * @param originalUser - Staff user
   * @param impersonatedUser - Impersonated user
   * @param action - Audit action type
   * @param details - Action details
   * @returns Promise resolving to created audit entry
   */
  private async createAuditEntry(
    sessionId: string,
    originalUser: UserInfo,
    impersonatedUser: UserInfo,
    action: AuditActionType,
    details: any
  ): Promise<ImpersonationAuditLogEntity | null> {
    try {
      const entity = await this.dataFetchService.createEntityInstance<ImpersonationAuditLogEntity>(
        EntityNames.ImpersonationAuditLogs
      );

      if (!entity) {
        throw new Error('Failed to create audit log entity');
      }

      entity.ImpersonationSessionID = sessionId;
      entity.OriginalUser_ID = originalUser.ID;
      entity.ImpersonatedUser_ID = impersonatedUser.ID;
      entity.AuditAction = action;
      entity.ActionDetails = JSON.stringify(details);
      entity.ClientIP = await this.utils.getClientIP();
      entity.UserAgent = this.getUserAgent();

      const success = await entity.Save();
      if (!success) {
        throw new Error('Failed to save audit log entry');
      }

      return entity;
    } catch (error) {
      console.error('Error creating audit entry:', error);
      return null;
    }
  }

  /**
   * Gets filtered audit logs based on criteria
   * @param filters - Filter criteria
   * @returns Promise resolving to filtered audit entries
   */
  private async getFilteredAuditLogs(filters?: {
    dateRange?: { start: Date; end: Date };
    userId?: string;
    sessionId?: string;
    actionType?: AuditActionType;
  }): Promise<ImpersonationAuditEntry[]> {
    try {
      // Build filter conditions
      const filterConditions: string[] = [];

      if (filters?.dateRange) {
        const startDate = filters.dateRange.start.toISOString();
        const endDate = filters.dateRange.end.toISOString();
        filterConditions.push(`Timestamp >= '${startDate}' AND Timestamp <= '${endDate}'`);
      }

      if (filters?.userId) {
        filterConditions.push(`(OriginalUser_ID = '${filters.userId}' OR ImpersonatedUser_ID = '${filters.userId}')`);
      }

      if (filters?.sessionId) {
        filterConditions.push(`ImpersonationSessionID = '${filters.sessionId}'`);
      }

      if (filters?.actionType) {
        filterConditions.push(`AuditAction = '${filters.actionType}'`);
      }

      const filter = filterConditions.length > 0 ? filterConditions.join(' AND ') : '';

      // Use parallel queries to get audit logs and users
      const [auditLogsResult, usersResult] = await this.dataFetchService.fetchMultipleEntities([
        {
          EntityName: EntityNames.ImpersonationAuditLogs,
          ExtraFilter: filter,
          OrderBy: 'Timestamp DESC',
          ResultType: 'entity_object'
        },
        {
          EntityName: EntityNames.Users,
          ResultType: 'entity_object'
        }
      ]);

      if (!auditLogsResult.success || !usersResult.success) {
        console.error('Error fetching filtered audit logs or users');
        return [];
      }

      const auditLogs = auditLogsResult.data as ImpersonationAuditLogEntity[];
      const users = usersResult.data as any[];
      
      // Create user lookup map for performance
      const userMap = new Map();
      users.forEach(user => userMap.set(user.ID, user.Name));

      return auditLogs.map(log => ({
        id: log.ID!,
        impersonationSessionId: log.ImpersonationSessionID,
        originalUserId: log.OriginalUser_ID,
        originalUserName: userMap.get(log.OriginalUser_ID) || 'Unknown User',
        impersonatedUserId: log.ImpersonatedUser_ID,
        impersonatedUserName: userMap.get(log.ImpersonatedUser_ID) || 'Unknown User',
        auditAction: log.AuditAction as AuditActionType,
        actionDetails: this.parseActionDetails(log.ActionDetails),
        timestamp: new Date(log.Timestamp),
        clientIP: log.ClientIP,
        userAgent: log.UserAgent
      }));
    } catch (error) {
      console.error('Error fetching filtered audit logs:', error);
      return [];
    }
  }

  /**
   * Parses action details JSON string safely
   * @param actionDetailsJson - JSON string
   * @returns Parsed object or empty object
   */
  private parseActionDetails(actionDetailsJson: string | null): any {
    if (!actionDetailsJson) {
      return {};
    }

    try {
      return JSON.parse(actionDetailsJson);
    } catch (error) {
      console.error('Error parsing action details:', error);
      return { error: 'Invalid JSON', raw: actionDetailsJson };
    }
  }

  /**
   * Gets user agent string
   * @returns User agent string or null
   */
  private getUserAgent(): string | null {
    return typeof navigator !== 'undefined' ? navigator.userAgent : null;
  }
}